import { Helmet } from 'react-helmet-async';
import serialize from 'serialize-javascript';

import type {
  CountryCodesAlpha2,
  TemplateData,
} from '@xing-com/crate-common-graphql-types';
import { JobRemoteOptionValue } from '@xing-com/crate-common-graphql-types';
import type { DescriptionFragment } from '@xing-com/crate-jobs-graphql';
import type { JobDetails } from '@xing-com/crate-jobs-helpers';

type Props = {
  job: JobDetails;
};

const concatenateTemplateData = (description: TemplateData): string => `
    ${description.genericDescription}
    ${description.companyDescriptionTitle}
    ${description.companyDescriptionContent}
    ${description.responsibilityTitle}
    ${description.responsibilityContent}
    ${description.skillsTitle}
    ${description.skillsContent}
    ${description.weOfferTitle}
    ${description.weOfferContent}
    ${description.contactInfoTitle}
    ${description.contactInfoContent}
  `;

const getJobDescription = (description: DescriptionFragment): string => {
  switch (description.__typename) {
    case 'HtmlDescription':
      return description.content ?? '';
    case 'TemplateData':
      return concatenateTemplateData(description);
    case 'ExternalUrlDescription':
      return description.htmlContent;
    default:
      return '';
  }
};

const getUniqueCountries = (
  locations: JobDetails['locations']
): { '@type': string; name: CountryCodesAlpha2 }[] => {
  return [
    ...new Set(
      locations
        .map((location) => location.country?.countryCode)
        .filter((code) => !!code)
    ),
  ].map((countryCode) => ({
    '@type': 'Country',
    name: countryCode,
  }));
};

export const StructuredData: React.FC<Props> = ({ job }) => {
  const {
    activatedAt,
    activeUntil,
    application,
    company,
    description,
    facts: { employmentType },
    highlights,
    industry,
    locations,
    title,
  } = job;

  const hiringOrganization = {
    '@type': 'Organization',
    name: company?.name,
    url: company?.link,
    logo: company?.logo,
  };

  const jobLocations = locations.map((location) => ({
    '@type': 'Place',
    address: {
      '@type': 'PostalAddress',
      streetAddress: location.street,
      addressLocality: location.city,
      addressRegion: location.region,
      postalCode: location.zipCode,
      addressCountry: location.country?.countryCode,
    },
  }));

  // Using https://developers.google.com/search/docs/data-types/job-posting#structured-data-type-definitions
  const jobPostingSchema = {
    '@context': 'https://schema.org/',
    '@type': 'JobPosting',
    title,
    description: getJobDescription(description),
    datePosted: activatedAt,
    directApply: application.type === 'JobXingApplication',
    validThrough: activeUntil,
    industry: industry?.name,
    employmentType: employmentType?.name,
    hiringOrganization,
    jobLocation: jobLocations,
    ...(highlights.remoteOption === JobRemoteOptionValue.FullRemote
      ? {
          jobLocationType: 'TELECOMMUTE',
          applicantLocationRequirements: getUniqueCountries(locations),
        }
      : { jobLocationType: undefined }),
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {serialize(jobPostingSchema, { isJSON: true })}
      </script>
    </Helmet>
  );
};
